import { useCallback, useMemo, type ReactNode } from "react"

import { useSidebarItems } from "~/hooks/useSidebarItems"

export type NavModule = {
  /** Top-level sidebar module, e.g. "Insights". */
  name: string
  /** Sub-item the route falls under, e.g. "Inventory". */
  pageName?: string
  icon: (color: string) => ReactNode
}

// Match on full path segments so sibling paths don't collide
// (e.g. "/product-management/inbox" must not activate "/inbox").
export const isPathActive = (location: string, pathName: string) =>
  location === pathName || location.startsWith(pathName + "/")

/** Resolves a route (path + optional query) to its sidebar module. Longest matching path wins. */
export function useResolveNavModule() {
  const { topSidebarItems, bottomSidebarItems } = useSidebarItems()

  const candidates = useMemo(
    () =>
      [...topSidebarItems, ...bottomSidebarItems]
        .flatMap((item) => {
          const module = { name: item.name, icon: item.icon }
          return [
            ...[item.pathName, ...(item.matchPaths ?? [])].map((path) => ({ path, module })),
            ...(item.subItems ?? []).map((sub) => ({
              path: sub.pathName,
              module: { ...module, pageName: sub.name },
            })),
          ]
        })
        .sort((a, b) => b.path.length - a.path.length),
    [topSidebarItems, bottomSidebarItems],
  )

  return useCallback(
    (route: string): NavModule | undefined => {
      const pathname = route.split("?")[0] ?? route
      return candidates.find(({ path }) => isPathActive(pathname, path))?.module
    },
    [candidates],
  )
}
